import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";

import thinkLogo from "../../assets/think-logo-192.png";
import Icons from "../common/Icons";
import { FacilitatorCommandBand } from "../FacilitatorShell/FacilitatorCommandBand";
import { FacilitatorContextBar } from "../FacilitatorShell/FacilitatorContextBar";
import { useFacilitatorContext } from "../FacilitatorShell/FacilitatorContextProvider";
import { statusTerm } from "../FacilitatorShell/statusVocabulary";
import { CancelWarning } from "../Profile/CancelWarning";

/**
 * The Facilitator OS surfaces: the paths that carry the persistent context
 * strip. Profile and the leaderboard sit outside the operating loop, so the
 * strip does not follow the facilitator there and claim a room context for a
 * page that has nothing to do with one.
 *
 * `/admin/manage` REPLACES `/admin/students` and `/admin/squads` on this list.
 * Those two paths are still routed, as redirects into Manage, and a redirect
 * never renders: it is not a surface, so it carries no context strip and does
 * not belong here.
 */
export const OS_SURFACE_PATHS = [
  "/admin/prep",
  "/admin/dashboard",
  "/admin/deliverables",
  "/admin/manage",
];

// Sidebar custom styles for cosmic aesthetic (matching student platform)
const sidebarStyles = `
  @keyframes subtleGlow {
    0%, 100% {
      box-shadow: inset 0 0 20px rgba(171, 235, 247, 0.03);
    }
    50% {
      box-shadow: inset 0 0 30px rgba(171, 235, 247, 0.06);
    }
  }

  @keyframes logoFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-2px); }
  }

  .sidebar-container {
    animation: subtleGlow 8s ease-in-out infinite;
    background: linear-gradient(180deg, #1a2b4b 0%, #152441 50%, #132039 100%);
  }

  .sidebar-logo {
    animation: logoFloat 4s ease-in-out infinite;
  }

  .menu-item-active {
    background: linear-gradient(90deg, rgba(255, 90, 95, 0.15) 0%, transparent 100%);
    border-left: 2px solid #FF5A5F;
  }

  .menu-item-hover:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

/**
 * ONE NAV ENTRY. Declared at MODULE SCOPE, and that is load-bearing rather than
 * tidiness.
 *
 * It used to be declared inside AdminSidebar's body, which makes React see a
 * brand new component type on every render and therefore unmount and remount
 * every nav button. That was survivable while the sidebar only re-rendered on a
 * route change. It stopped being survivable the moment this component started
 * reading the shell context, because the glance envelope changes on every poll
 * tick: a keyboard user resting on a nav button would have had focus dropped to
 * <body> once a minute, silently, forever.
 */
const MenuItem = ({ item, isActive, isCollapsed, badge, onSelect }) => {
  if (item.external) {
    return (
      <a
        href={item.path}
        target="_blank"
        rel="noopener noreferrer"
        className={`
          w-full flex items-center gap-3 py-2.5 px-3 rounded-md
          transition-all duration-200 ease-out group
          text-text-lv4 menu-item-hover
        `}
      >
        <div className="flex items-center justify-center w-5 h-5 transition-transform duration-200 group-hover:scale-105">
          <Icons type={item.icon} className="w-5 h-5" color="#B4C1E4" />
        </div>
        {!isCollapsed && (
          <span className="text-sm font-medium tracking-wide text-text-lv4 group-hover:text-text-lv2 transition-colors duration-200">
            {item.title}
          </span>
        )}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      aria-current={isActive ? "page" : undefined}
      className={`
        w-full flex items-center gap-3 py-2.5 px-3 rounded-md
        transition-all duration-200 ease-out group focus-visible:focus-ring
        ${isActive ? "menu-item-active text-persimmon" : "text-text-lv4 menu-item-hover"}
      `}
    >
      <div
        className={`
          relative flex items-center justify-center w-5 h-5
          transition-transform duration-200
          ${isActive ? "scale-110" : "group-hover:scale-105"}
        `}
      >
        <Icons
          type={item.icon}
          className="w-5 h-5"
          color={isActive ? "#FF5A5F" : "#B4C1E4"}
        />
        {/* Collapsed rail: the count has nowhere to sit beside the label, so it
            becomes a dot. The number itself is still announced, from the
            screen-reader clause below, so nothing is lost by not seeing it. */}
        {badge !== null && isCollapsed ? (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-persimmon"
          />
        ) : null}
      </div>
      {!isCollapsed && (
        <span
          className={`
            text-sm font-medium tracking-wide
            transition-colors duration-200
            ${isActive ? "text-persimmon" : "text-text-lv4 group-hover:text-text-lv2"}
          `}
        >
          {item.title}
        </span>
      )}
      {badge !== null && !isCollapsed ? (
        <span
          aria-hidden="true"
          className="ml-auto rounded-full bg-persimmon/20 px-2 py-0.5 text-xs font-semibold text-persimmon"
        >
          {badge}
        </span>
      ) : null}
      {/* The count belongs in the button's accessible name, not only in a pill.
          It also says WHAT it counts: the aggregate is cross-level, so "3" on
          its own would read as "3 on this level". */}
      {badge !== null ? (
        <span className="sr-only">
          {/* FE-9.6: the shell chrome was the one OS surface no chunk gate ever
              read, and this phrase was canonical only because the sidebar and
              Review happened to agree. It reads from the module now, so the
              nav's screen-reader name and Review's toolbar cannot drift. */}
          , {badge} {statusTerm("awaiting_review")} across all levels
        </span>
      ) : null}
    </button>
  );
};

const AdminSidebar = ({ children }) => {
  const { isCancel } = useSelector((state) => state.profile);
  const [movePage, setMovePage] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(1);
  const [isWarningDialog, setIsWarningDialog] = useState(false);

  // The shell may be absent: AdminSidebar is also rendered by tests and by the
  // non-facilitator admin surfaces' own harnesses. A missing provider means no
  // badge and no context strip, never a crash.
  const shell = useFacilitatorContext();
  /**
   * ZERO AND UNKNOWN COLLAPSE HERE, AND ONLY HERE, AND ONLY BECAUSE THIS
   * RENDER MAKES NO CLAIM.
   *
   * `reviewPending` is deliberately three-valued at the data layer: a number is
   * the server's count, `0` is "the server says nothing is waiting", and `null`
   * is "the shell does not know" (no response yet, a failed read, or a scope it
   * cannot attribute to this room). `readPendingTotal` in `reviewPending.js`
   * keeps that distinction and stays the source of truth for consumers that
   * need it. F3.1 will need it: after a save it has to tell "the queue is now
   * empty" apart from "the refresh did not come back".
   *
   * The nav icon needs neither. Both states render NOTHING: no pill, no
   * screen-reader clause, no aria-label, no title. Every claim-bearing element
   * below is gated on `badge !== null`, so the button's accessible name is
   * exactly "review" on both paths. Collapsing two states into one render is
   * only safe when that render asserts nothing about either, which is the case
   * here and is pinned by a test. If a tooltip or an all-clear affordance is
   * ever added to this item, the collapse stops being safe and this must take
   * the three-valued state instead of a number-or-null.
   */
  const reviewBadge =
    typeof shell?.reviewPending === "number" && shell.reviewPending > 0
      ? shell.reviewPending
      : null;

  const isOsSurface = OS_SURFACE_PATHS.includes(location.pathname);

  // FOCUS ON SURFACE SWITCH. A facilitator who tabs to "review" and presses
  // enter is, without this, left with focus still on the sidebar: their next Tab
  // walks the nav again instead of entering the surface they just opened.
  //
  // The intent ref is what keeps this honest. Focusing on every pathname change
  // would steal focus on first paint and on a browser back button, neither of
  // which is a request to go somewhere. Only a click on a nav item arms it.
  const navIntentRef = useRef(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!navIntentRef.current) return;
    navIntentRef.current = false;
    contentRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const handleSelect = useCallback(
    (item) => {
      setMovePage(item.id);

      if (location.pathname === "/admin/profile" && isCancel && item.id !== 8) {
        // The warning dialog owns the navigation now, so nothing has moved yet.
        navIntentRef.current = false;
        setIsWarningDialog(true);
        return;
      }

      // ARM ONLY WHEN THE PATHNAME WILL ACTUALLY CHANGE. Clicking the nav item
      // for the surface you are already on does not re-run the effect below, so
      // an intent armed here would stay armed indefinitely and then spend itself
      // on the next pathname change from any other cause, including the browser
      // back button. That is precisely the focus theft the ref exists to prevent.
      navIntentRef.current = location.pathname !== item.path;
      navigate(item.path);
    },
    [location.pathname, isCancel, navigate],
  );

  /**
   * THE FOUR SURFACES, in the order of the loop they form:
   *   prepare the level, coach the live work, review the work, manage the room.
   *
   * All four are here now. `manage` opens `/admin/manage`, which this PR
   * builds, and the two surfaces it replaces (`students`, `squads`) leave the
   * nav entirely. Their paths stay routed as redirects into Manage, so every
   * bookmark and every deep link still lands somewhere a facilitator can use.
   *
   * IDS ARE STABLE IDENTIFIERS, NOT POSITIONS, so 2 and 3 are RETIRED rather
   * than reused. `CancelWarning` receives `mainMenuItems.concat(bottomMenuItems)`
   * below and resolves a pending navigation out of it by id: a `manage` entry
   * that took id 2 would send a facilitator who cancelled mid-flight to the
   * surface the id used to mean. That is exactly the bug a stable-id list
   * exists to prevent, so manage takes 6, which no item has ever used.
   *
   * Paths for the surviving three are unchanged. `review` is
   * `/admin/deliverables` and `coach` is `/admin/dashboard`.
   */
  const mainMenuItems = useMemo(
    () => [
      {
        id: 1,
        title: "dashboard",
        path: "/admin/dashboard",
        aliases: ["/admin/dashboard", "/admin/prep"],
        icon: "home",
      },
      {
        id: 4,
        title: "review",
        path: "/admin/deliverables",
        icon: "levelDeliverable",
        badgeKey: "review",
      },
      { id: 6, title: "manage", path: "/admin/manage", icon: "settings" },
      {
        id: 5,
        title: "leaderboard",
        path: "/admin/leaderboard",
        icon: "podium",
      },
    ],
    [],
  );

  const bottomMenuItems = useMemo(
    () => [
      { id: 8, title: "profile", path: "/admin/profile", icon: "user" },
      // { id: 9, title: "tutorials", path: "/admin/tutorials", icon: "question" },
      {
        id: 10,
        title: "feedback",
        path: "mailto:sam@thinccc.org",
        icon: "provideFeedback",
        external: true,
      },
    ],
    [],
  );

  useEffect(() => {
    const items = [...mainMenuItems, ...bottomMenuItems];
    const activeItem = items.find((item) =>
      (item.aliases || [item.path]).includes(location.pathname),
    );
    setActive(activeItem?.id);
  }, [location.pathname, bottomMenuItems, mainMenuItems]);

  const renderItem = (item) => (
    <MenuItem
      key={item.id}
      item={item}
      isActive={active === item.id}
      isCollapsed={isCollapsed}
      badge={item.badgeKey === "review" ? reviewBadge : null}
      onSelect={handleSelect}
    />
  );

  return (
    <>
      <style>{sidebarStyles}</style>
      <div className="flex h-dvh overflow-y-auto scrollbar-hidden">
        <div
          className={`
            sidebar-container transition-all duration-300 w-auto
            border-r border-white/5
          `}
        >
          <div className="flex flex-col py-6 px-2 h-full">
            {/* Think Logo */}
            <div className="flex justify-center mb-8 px-2">
              <div
                className={`
                  sidebar-logo flex items-center justify-center
                  transition-all duration-300
                  ${isCollapsed ? "w-8 h-8" : "w-9 h-9"}
                `}
              >
                <img
                  src={thinkLogo}
                  alt="Think"
                  className="w-full h-full object-contain"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(171, 235, 247, 0.25))",
                  }}
                />
              </div>
            </div>

            {/* Main Menu Items */}
            <nav
              aria-label="facilitator surfaces"
              className="flex-1 px-1 space-y-1"
            >
              {mainMenuItems.map(renderItem)}
            </nav>

            {/* Subtle divider */}
            <div className="mx-3 my-3">
              <div
                className="h-px w-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(171, 235, 247, 0.15) 50%, transparent 100%)",
                }}
              />
            </div>

            {/* Bottom Menu Items */}
            <div className="px-1 space-y-1">
              {bottomMenuItems.map(renderItem)}
            </div>

            {/* Bottom divider */}
            <div className="mx-3 my-3">
              <div
                className="h-px w-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.08) 50%, transparent 100%)",
                }}
              />
            </div>

            {/* Collapse Button */}
            {/* The visible `collapse` label is dropped when the rail is
                collapsed, which leaves a button whose only child is an svg. The
                aria-label therefore has to be unconditional, and aria-expanded
                carries the state the disappearing label used to imply. */}
            <button
              type="button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? "expand the rail" : "collapse the rail"}
              aria-expanded={!isCollapsed}
              className={`
                focus-visible:focus-ring
                flex items-center gap-2 px-3 py-2 mx-1 rounded-md
                text-text-lv4 transition-all duration-200
                hover:bg-white/5 hover:text-text-lv2
                group
              `}
            >
              <div className="transition-transform duration-200 group-hover:scale-105">
                <Icons
                  type={isCollapsed ? "show" : "hide"}
                  className="w-4 h-4"
                  color="#B4C1E4"
                />
              </div>
              {!isCollapsed && (
                <span className="text-xs font-medium tracking-wider uppercase">
                  collapse
                </span>
              )}
            </button>
          </div>
        </div>
        {/* THE SURFACE COLUMN. The context strip sits above the surface rather
            than inside it, so it survives every navigation between surfaces and
            no page has to render its own copy. The scroll lives here, on the
            column, which is why the two surfaces this chunk touches drop their
            own `h-dvh` for `h-full`: a viewport-height child inside a
            viewport-height column is the one shape that produces two scrollbars.

            tabIndex -1 makes this a focus target without putting it in the tab
            order. It is where focus lands after a nav click (see the effect in
            this component) so a keyboard user enters the surface they chose. */}
        <div className="flex min-w-0 flex-1 flex-col">
          {isOsSurface && shell ? <FacilitatorContextBar /> : null}
          {shell &&
          ["/admin/dashboard", "/admin/prep"].includes(location.pathname) ? (
            <FacilitatorCommandBand />
          ) : null}
          <div
            ref={contentRef}
            tabIndex={-1}
            className="min-h-0 flex-1 overflow-y-auto scrollbar-hidden outline-none"
          >
            {children}
          </div>
        </div>

        {/* Cancel Warning Dialog */}
        <CancelWarning
          isOpen={isWarningDialog}
          active={movePage}
          navigate={navigate}
          items={mainMenuItems.concat(bottomMenuItems)}
          setActive={setActive}
          onClose={() => setIsWarningDialog(false)}
        />
      </div>
    </>
  );
};

export default AdminSidebar;
